import React, { useCallback, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';

import { useTabManager, useTabDisplayActiveId } from '../../../context/tab-manager-context';
import { CollectionTab } from '../CollectionTab';
import { ViewsetsMenuButton } from '../TabOptions';

import { TabItemV3 } from './TabItemV3';

import { Icon } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';

const TabContainerWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.v2.tab.containerBackgroundColor};
  border-bottom: 1px solid ${({ theme }) => theme.v2.tab.borderColor};
  height: 50px;
  position: relative;
`;

const TabsScrollContainer = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  min-width: 0;
  padding-right: 100px; /* Space for action buttons */
`;

const TabsList = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  min-width: fit-content;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  background-color: ${({ theme }) => theme.v2.tab.containerBackgroundColor};
  z-index: 2;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  height: 100%;
  min-width: 50px;
  &:focus {
    outline: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: ${({ theme }) => theme.v2.tab.emptyStateTextColor};
  font-size: 14px;
`;

const VIRTUALIZATION_THRESHOLD = 20;

export const TabContainerV3: React.FC = () => {
  const { tabs, switchTab, activeCollectionId } = useTabManager();
  const displayActiveId = useTabDisplayActiveId();
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const handleTabClick = useCallback(
    (tabId: string) => {
      switchTab(tabId);
    },
    [switchTab],
  );

  const handleDropdownToggle = useCallback((tabId: string) => {
    setOpenDropdownId((prev) => (prev === tabId ? null : tabId));
  }, []);

  const handleCloseDropdown = useCallback(() => {
    setOpenDropdownId(null);
  }, []);

  // const handleAddNewTab = useCallback(async () => {
  //   await addNewTab();
  // }, [addNewTab]);

  const hasCollection = !!activeCollectionId;
  const shouldVirtualize = tabs.length + (hasCollection ? 1 : 0) > VIRTUALIZATION_THRESHOLD;

  const tabItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    // Add CollectionTab as first item if collection exists
    if (hasCollection) {
      items.push(<CollectionTab key="collection-tab" />);
    }

    // Add regular tabs
    tabs.forEach((tab) => {
      items.push(
        <TabItemV3
          key={tab.id}
          tab={tab}
          isActive={displayActiveId === tab.id}
          onClick={handleTabClick}
          description={tab.description}
          isDirty={!!tab.isDirty}
          isDropdownOpen={openDropdownId === tab.id}
          onDropdownToggle={handleDropdownToggle}
          onCloseDropdown={handleCloseDropdown}
        />,
      );
    });

    return items;
  }, [
    tabs,
    displayActiveId,
    handleTabClick,
    hasCollection,
    openDropdownId,
    handleDropdownToggle,
    handleCloseDropdown,
  ]);

  if (tabs.length === 0) {
    return (
      <TabContainerWrapper data-testid="tab-container">
        <EmptyState>No tabs open</EmptyState>
        <ActionButtonsContainer>
          <ViewsetsMenuButton
            trigger={
              <ActionButton aria-label="Viewsets Menu">
                <Icon name={ICONS.Chevron} width="20" height="20" />
              </ActionButton>
            }
          />
        </ActionButtonsContainer>
      </TabContainerWrapper>
    );
  }

  return (
    <TabContainerWrapper data-testid="tab-container">
      <TabsScrollContainer>
        {shouldVirtualize ? (
          <Virtuoso
            data-testid="virtualized-tab-list"
            style={{ height: '50px', width: '100%' }}
            totalCount={tabs.length + (hasCollection ? 1 : 0)}
            itemContent={(index) => {
              // First item is CollectionTab if collection exists
              if (hasCollection && index === 0) {
                return <CollectionTab key="collection-tab" />;
              }

              // Adjust index for regular tabs (subtract 1 if collection exists)
              const tabIndex = hasCollection ? index - 1 : index;
              const tab = tabs[tabIndex];
              if (!tab) return null;
              return (
                <TabItemV3
                  key={tab.id}
                  tab={tab}
                  isActive={displayActiveId === tab.id}
                  onClick={handleTabClick}
                  description={tab.description}
                  isDirty={!!tab.isDirty}
                  isDropdownOpen={openDropdownId === tab.id}
                  onDropdownToggle={handleDropdownToggle}
                  onCloseDropdown={handleCloseDropdown}
                />
              );
            }}
            components={{
              List: React.forwardRef<HTMLDivElement>((props, ref) => (
                <TabsList ref={ref} {...props} />
              )),
            }}
          />
        ) : (
          <TabsList>{tabItems}</TabsList>
        )}
      </TabsScrollContainer>
      <ActionButtonsContainer>
        {/* <ActionButton onClick={handleAddNewTab} aria-label="Add New Tab">
          <Icon name={ICONS.Plus} width="15" height="15" />
        </ActionButton> */}
        <ViewsetsMenuButton
          trigger={
            <ActionButton aria-label="Viewsets Menu">
              <Icon name={ICONS.Chevron} width="10" height="10" />
            </ActionButton>
          }
        />
      </ActionButtonsContainer>
    </TabContainerWrapper>
  );
};
